import { type Swatches } from '@Types/colorSwatch';

/**
 * Given a color value (which may be a plain hex/rgb string OR a CSS variable
 * key like "--primary" or a swatch key like "brand-red"), resolve it to a
 * concrete color string suitable for use in `style` props.
 *
 * Resolution order:
 *  1. Falsy / empty string → return undefined
 *  2. Direct key match in swatches → return swatch.color
 *  3. CSS variable name (starts with "--") → return the variable reference
 *     so the browser resolves it at paint time: `var(--foo)`
 *  4. Otherwise assume it is already a concrete color and return as-is.
 */
export function resolveCssColor(
  color: string | undefined,
  swatches: Swatches,
): string | undefined {
  if (!color) return undefined;

  // Swatch key lookup (e.g. "brand-red", "--primary" stored as swatch keys)
  if (swatches[color] !== undefined) {
    return resolveCssColor(swatches[color].color, swatches);
  }

  // CSS variable name — wrap so the browser resolves it
  if (color.startsWith('--')) {
    return `var(${color})`;
  }

  // Concrete color value (hex, rgb, hsl, named color, etc.)
  return color;
}

/**
 * Same as `resolveCssColor` but also returns the human-readable name of the
 * resolved color (from the swatch entry if one was found).
 *
 * Returns `{ color, name }` where `name` may be undefined when no swatch
 * entry was matched.
 */
export function resolveCssColorWithName(
  color: string | undefined,
  swatches: Swatches,
): { color: string; name?: string } {
  if (!color) return { color: '' };

  if (swatches[color] !== undefined) {
    const swatch = swatches[color];
    // Recursively resolve in case the swatch itself points to another key
    const resolved = resolveCssColor(swatch.color, swatches);
    return { color: resolved ?? '', name: swatch.name };
  }

  if (color.startsWith('--')) {
    return { color: `var(${color})` };
  }

  return { color };
}

/**
 * Normalises a raw color string coming from user text input into a
 * consistent hex value.
 *
 * Rules:
 *  - Already a valid 6-digit hex  → return as-is (lowercased, with #)
 *  - 3-digit hex shorthand        → expand to 6 digits
 *  - Missing leading #            → prepend it
 *  - CSS variable name            → return as-is (resolved elsewhere)
 *  - Anything else unrecognised   → return the original string unchanged
 */
export function resolveHexValue(raw: string): string {
  if (!raw) return raw;

  const trimmed = raw.trim();

  // CSS variable — pass through; resolution happens in qbit-utils
  if (trimmed.startsWith('--')) return trimmed;

  // Strip leading # if present so we can work with the bare digits
  const withoutHash = trimmed.startsWith('#') ? trimmed.slice(1) : trimmed;

  // 3-digit shorthand → expand  e.g. "f0a" → "ff00aa"
  if (/^[0-9a-fA-F]{3}$/.test(withoutHash)) {
    const expanded = withoutHash
      .split('')
      .map((c) => c + c)
      .join('');
    return `#${expanded.toLowerCase()}`;
  }

  // 6-digit hex
  if (/^[0-9a-fA-F]{6}$/.test(withoutHash)) {
    return `#${withoutHash.toLowerCase()}`;
  }

  // 8-digit hex with alpha channel
  if (/^[0-9a-fA-F]{8}$/.test(withoutHash)) {
    return `#${withoutHash.toLowerCase()}`;
  }

  // Unrecognised — return unchanged so the caller can decide what to do
  return trimmed;
}
